import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { debounce } from 'lodash';
import { EntityType, Individual, Organization } from 'utils';
import { adminGetAllOrganizations, adminViewOrganization } from '../../../admin.organizations.api';
import { adminGetIndividuals } from '../../../admin.individuals.api';
import { addFp1DeviceNote, updateFp1Device } from '../fp1.api';
import {
    type Fp1Device,
    type UpdateFp1DeviceParams,
    fp1EffectiveAthleteName,
    fp1EffectiveAthleteUuid,
    fp1EffectiveOrganizationName,
    fp1EffectiveOrganizationUuid,
    fp1StatusRetainsBuyerAssignment,
} from '../fp1.types';

const emptyImageUrls = {
    avatar: null,
    original: null,
    '200': null,
    '350': null,
    '500': null,
    '1000': null,
} as Organization['image_urls'];

function minimalOrganization(uuid: string, name: string): Organization {
    return {
        uuid,
        name: name.trim() || 'Organization',
        slug: '',
        about_us: null,
        logo_path: null,
        logo_url: null,
        banner_url: null,
        video_url: null,
        mux_playback_id: undefined,
        image_urls: emptyImageUrls,
        object: EntityType.ORGANIZATION,
        marketplace_enabled: false,
    };
}

interface Props {
    open: boolean;
    device: Fp1Device;
    onClose: () => void;
    onSaved: () => void;
}

const Fp1ModifyAssignmentDialog: React.FC<Props> = ({ open, device, onClose, onSaved }) => {
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [individuals, setIndividuals] = useState<Individual[]>([]);
    const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
    const [selectedAthlete, setSelectedAthlete] = useState<Individual | null>(null);
    // Athletes belong to a specific org and the device stores a *user* uuid, so the
    // current assignment isn't necessarily one of the fetched org individuals. Track
    // whether the admin actually touched the athlete field; only then do we send
    // athlete_uuid, otherwise an untouched save would wipe the existing athlete.
    const [athleteDirty, setAthleteDirty] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const assignmentAllowed = fp1StatusRetainsBuyerAssignment(device.status);

    const fetchOrganizations = useMemo(
        () =>
            debounce((searchTerm: string) => {
                adminGetAllOrganizations(searchTerm, 500)
                    .then((response) =>
                        setOrganizations(
                            response.data.data.filter(
                                (o) => !o.name.includes("'s Personal Organization"),
                            ),
                        ),
                    )
                    .catch(() => setOrganizations([]));
            }, 300),
        [],
    );

    // Source athletes the same way the Pods page does: org-scoped individuals that
    // have a managing user. This yields exactly one option per athlete in the org
    // (vs. the global /admin/users search, which returned every account matching a
    // name regardless of org). Without an org there is nothing to scope to.
    const fetchIndividuals = useMemo(
        () =>
            debounce((searchTerm: string, orgUuid?: string) => {
                if (!orgUuid) {
                    setIndividuals([]);
                    return;
                }
                adminGetIndividuals({
                    'filter[organization]': orgUuid,
                    'filter[name]': searchTerm,
                    'filter[has_managing_user]': 1,
                })
                    .then((response) => setIndividuals(response.data.data))
                    .catch(() => setIndividuals([]));
            }, 300),
        [],
    );

    const resolveInitialOrg = useCallback(async () => {
        const orgUuid = fp1EffectiveOrganizationUuid(device);
        if (!orgUuid) {
            setSelectedOrg(null);
            return;
        }
        try {
            const res = await adminViewOrganization(orgUuid);
            setSelectedOrg(res.data);
        } catch {
            setSelectedOrg(
                minimalOrganization(
                    orgUuid,
                    fp1EffectiveOrganizationName(device) || 'Current organization',
                ),
            );
        }
    }, [device]);

    useEffect(() => {
        if (!open) {
            return;
        }
        setError(null);
        setSaving(false);
        setSelectedAthlete(null);
        setAthleteDirty(false);
        setLoading(true);
        fetchOrganizations('');
        const initialOrgUuid = fp1EffectiveOrganizationUuid(device) ?? undefined;
        fetchIndividuals('', initialOrgUuid);
        resolveInitialOrg().finally(() => setLoading(false));
    }, [open, device, fetchOrganizations, fetchIndividuals, resolveInitialOrg]);

    useEffect(() => {
        return () => {
            fetchOrganizations.cancel();
            fetchIndividuals.cancel();
        };
    }, [fetchOrganizations, fetchIndividuals]);

    const orgOptions = useMemo(() => {
        const byUuid = new Map<string, Organization>();
        organizations.forEach((o) => byUuid.set(o.uuid, o));
        if (selectedOrg) {
            byUuid.set(selectedOrg.uuid, selectedOrg);
        }
        return Array.from(byUuid.values());
    }, [organizations, selectedOrg]);

    // De-dupe by managing user, not individual uuid: a person can have more than one
    // athlete profile (individual) in the same org. Those have distinct individual
    // uuids but the same managing_user_uuid, and since the device is assigned to the
    // *user* they collapse to one identical-looking, identical-acting option.
    const individualOptions = useMemo(() => {
        const keyFor = (i: Individual) => i.managing_user_uuid ?? i.uuid;
        const byUser = new Map<string, Individual>();
        individuals.forEach((i) => {
            if (!byUser.has(keyFor(i))) {
                byUser.set(keyFor(i), i);
            }
        });
        if (selectedAthlete && !byUser.has(keyFor(selectedAthlete))) {
            byUser.set(keyFor(selectedAthlete), selectedAthlete);
        }
        return Array.from(byUser.values());
    }, [individuals, selectedAthlete]);

    const handleSave = async () => {
        if (!assignmentAllowed) {
            setError('Assignment can only be saved while the device status is Deployed.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload: UpdateFp1DeviceParams = {
                organization_uuid: selectedOrg?.uuid ?? null,
            };
            // The device stores a user uuid; resolve the chosen individual to its
            // managing user. Only send it when the admin touched the athlete field so
            // an untouched save preserves the existing assignment.
            if (athleteDirty) {
                payload.athlete_uuid = selectedAthlete?.managing_user_uuid ?? null;
            }
            await updateFp1Device(device.uuid, payload);
            const orgNote = selectedOrg ? `Org set to ${selectedOrg.name}` : 'Org cleared';
            const prevAthleteUuid = fp1EffectiveAthleteUuid(device);
            const prevAthleteName = fp1EffectiveAthleteName(device);
            const athleteNote = !athleteDirty
                ? null
                : selectedAthlete
                ? `Athlete set to ${selectedAthlete.name}`.trim()
                : prevAthleteUuid
                ? `Athlete cleared (was ${prevAthleteName || prevAthleteUuid})`
                : null;
            const noteParts = [orgNote, athleteNote].filter(Boolean);
            void addFp1DeviceNote(
                device.uuid,
                `Admin assignment update — ${noteParts.join('; ')}.`,
            );
            onSaved();
            onClose();
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
                'Failed to update assignment.';
            setError(typeof msg === 'string' ? msg : 'Failed to update assignment.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                }}
            >
                <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                    Modify assignment
                </Typography>
                <IconButton onClick={onClose} size="small" aria-label="close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Pod <strong>{device.serial_number}</strong> — set the organization and/or
                    athlete tied to this device, or clear either field.
                </Typography>

                {!assignmentAllowed && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        The server only keeps organization and athlete on devices in{' '}
                        <strong>Deployed</strong> status. For other statuses, assignment is cleared
                        automatically. Change status to Deployed first if you need to set assignment
                        here.
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 0.5 }}>
                        <Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 0.5 }}
                            >
                                Current organization
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {fp1EffectiveOrganizationUuid(device)
                                    ? fp1EffectiveOrganizationName(device) ||
                                      fp1EffectiveOrganizationUuid(device)
                                    : 'None'}
                            </Typography>
                            <FormControl fullWidth size="small">
                                <Autocomplete
                                    value={
                                        orgOptions.find((o) => o.uuid === selectedOrg?.uuid) ??
                                        selectedOrg
                                    }
                                    onChange={(_, newValue) => {
                                        setSelectedOrg(newValue);
                                        // Athletes are org-specific; changing the org
                                        // invalidates any prior athlete selection.
                                        setSelectedAthlete(null);
                                        setAthleteDirty(true);
                                        fetchIndividuals('', newValue?.uuid);
                                    }}
                                    onInputChange={(_, newInputValue, reason) => {
                                        if (reason !== 'reset') {
                                            fetchOrganizations(newInputValue);
                                        }
                                    }}
                                    options={orgOptions}
                                    getOptionKey={(option) => option.uuid}
                                    filterOptions={(opts) => opts}
                                    getOptionLabel={(option) =>
                                        option.subscription_tier?.name
                                            ? `${option.name} (${option.subscription_tier.name})`
                                            : option.name
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Organization"
                                            placeholder="Search…"
                                        />
                                    )}
                                />
                            </FormControl>
                            <Button
                                size="small"
                                sx={{ mt: 0.75 }}
                                onClick={() => {
                                    setSelectedOrg(null);
                                    setSelectedAthlete(null);
                                    setAthleteDirty(true);
                                    fetchIndividuals('', undefined);
                                }}
                                disabled={!selectedOrg}
                            >
                                Clear organization
                            </Button>
                        </Box>

                        <Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 0.5 }}
                            >
                                Current athlete
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {fp1EffectiveAthleteUuid(device)
                                    ? fp1EffectiveAthleteName(device) ||
                                      fp1EffectiveAthleteUuid(device)
                                    : 'None'}
                            </Typography>
                            <FormControl fullWidth size="small">
                                <Autocomplete
                                    value={
                                        individualOptions.find(
                                            (i) =>
                                                (i.managing_user_uuid ?? i.uuid) ===
                                                (selectedAthlete?.managing_user_uuid ??
                                                    selectedAthlete?.uuid),
                                        ) ?? selectedAthlete
                                    }
                                    onChange={(_, newValue) => {
                                        setSelectedAthlete(newValue);
                                        setAthleteDirty(true);
                                    }}
                                    onInputChange={(_, newInputValue, reason) => {
                                        if (reason === 'input') {
                                            fetchIndividuals(newInputValue, selectedOrg?.uuid);
                                        }
                                    }}
                                    options={individualOptions}
                                    getOptionKey={(option) =>
                                        option.managing_user_uuid ?? option.uuid
                                    }
                                    isOptionEqualToValue={(a, b) =>
                                        (a.managing_user_uuid ?? a.uuid) ===
                                        (b.managing_user_uuid ?? b.uuid)
                                    }
                                    getOptionLabel={(option) => option.name}
                                    disabled={!selectedOrg}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Athlete"
                                            placeholder={
                                                selectedOrg
                                                    ? 'Search…'
                                                    : 'Select an organization first'
                                            }
                                        />
                                    )}
                                />
                            </FormControl>
                            <Button
                                size="small"
                                sx={{ mt: 0.75 }}
                                onClick={() => {
                                    setSelectedAthlete(null);
                                    setAthleteDirty(true);
                                }}
                                disabled={!selectedAthlete}
                            >
                                Clear athlete
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || loading || !assignmentAllowed}
                >
                    {saving ? <CircularProgress size={22} /> : 'Save assignment'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default Fp1ModifyAssignmentDialog;
